import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthShell from '../../components/marketing/AuthShell';
import { useResetPassword } from '../../api/hooks';
import { tokenFromPaste } from '../../lib/resetToken';
import { plainError } from '../../lib/format';

/**
 * Where the emailed reset link lands.
 *
 * The backend mails `{FRONTEND_URL}/reset-password?token=…`, so this path is
 * not a design choice — it is the address in the email, and without a route
 * here every reset link this product has ever sent dead-ends on the login
 * page. The token is read from the URL and never shown or typed.
 *
 * No email field: the token identifies the account on its own, and asking for
 * an address as well would only add a way to get it wrong.
 *
 * IT ALSO ACCEPTS A PASTED LINK, which is not belt-and-braces. The backend
 * builds the email from its `FRONTEND_URL`, and that setting currently names
 * the API host rather than this app — so the link in the mail opens the
 * backend, which has no such page. The token is minted against the account and
 * does not care what host the link pointed at, so pasting the link in here
 * finishes the reset. When the backend setting is fixed this simply stops
 * being needed.
 */
const MIN_PASSWORD = 8;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const fromUrl = params.get('token') || '';
  /* What was pasted, when the link did not bring us here itself. */
  const [pasted, setPasted] = useState('');
  const token = fromUrl || tokenFromPaste(pasted) || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(null);
  const [failure, setFailure] = useState(null);
  const [reset, { loading }] = useResetPassword();

  const submit = async (e) => {
    e.preventDefault();
    setFailure(null);
    if (password.length < MIN_PASSWORD) {
      setFailure(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setFailure('The two passwords do not match.');
      return;
    }
    try {
      const { data } = await reset({ variables: { input: { token, newPassword: password } } });
      const r = data?.resetPassword;
      if (!r?.success) throw new Error(r?.message || 'The password was not changed');
      setDone(r.message || 'Your password is set.');
    } catch (err) {
      setFailure(plainError(err, 'We could not set the password'));
    }
  };

  return (
    <AuthShell
      aside={(
        <>
          <div>
            <h2>Choose a new password</h2>
            <p className="read">
              One you have not used elsewhere. It is the key to every client record this desk can reach.
            </p>
          </div>
          <span style={{ fontSize: 12, color: '#8b90a8' }}>© 2026 Arthaprajna Private Limited</span>
        </>
      )}
    >
      <h1>Set a new password</h1>

      {!token ? (
        <>
          <div className="sub">
            Paste the reset link from your email here. If it opened a page that was not this one, that is the
            link — copy it out of the address bar and paste it below; it still works.
          </div>
          <div className="field">
            <label>The reset link, or just the token from it</label>
            <input
              type="text"
              placeholder="https://…/reset-password?token=…"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
            />
          </div>
          {pasted.trim() && (
            <div className="auth-msg bad">
              That does not contain a reset token. Copy the whole link from the email.
            </div>
          )}
          <Link
            className="btn"
            style={{ width: '100%', marginTop: 12, display: 'block', textAlign: 'center' }}
            to="/forgot-password"
          >
            Send me a new link
          </Link>
        </>
      ) : done ? (
        <>
          <div className="auth-msg">{done}</div>
          <Link
            className="btn pri lg"
            style={{ width: '100%', marginTop: 18, display: 'block', textAlign: 'center' }}
            to="/login"
          >
            Sign in →
          </Link>
        </>
      ) : (
        <>
          <div className="sub">Pick something new. You will sign in with it straight after.</div>
          <form onSubmit={submit}>
            <div className="field">
              <label>New password</label>
              <input
                type="password"
                autoComplete="new-password"
                placeholder={`At least ${MIN_PASSWORD} characters`}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="field">
              <label>New password again</label>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <button className="btn pri lg" style={{ width: '100%', marginTop: 22 }} type="submit" disabled={loading}>
              {loading ? 'Please wait…' : 'Set the password →'}
            </button>
            {failure && <div className="auth-msg bad">{failure}</div>}
          </form>
        </>
      )}

      <div className="auth-alt">Not what you wanted? <Link to="/login">Back to sign in</Link></div>
    </AuthShell>
  );
}
